import React from "react";
const { ResponsivePie, PieCanvas } = require("@nivo/pie");
import PropTypes from "prop-types";
import { Button } from "primereact/button";
import Loader from "Components/Loader/loader";
import Modal from "Components/Modal/Modal";

const NivoPie = ({
  data,
  title,
  legendWidth,
  labelName,
  displayPercentage,
  graphHeight,
  colors,
  displayLegends,
}) => {
  const [dialogOpen, setDialogOpen] = React.useState(false);

  /**
   * Exporte le graphique en fichier Excel
   *
   * Librairie utilisée : https://www.npmjs.com/package/xlsx
   */
  const exportPieChartExcel = () => {
    import("xlsx").then((xlsx) => {
      const excelData = [...data];
      //? Ajout du total dans le tableau
      excelData.push({
        id: "TOTAL",
        value: data.reduce((a, b) => a + b.value, 0),
      });

      const workBook = xlsx.utils.book_new();
      const workSheetData = [
        [labelName || "Catégorie", "Nombre d'offres"],
        ...excelData.map((row) => [row.id, row.value]),
      ];
      const workSheet = xlsx.utils.aoa_to_sheet(workSheetData);
      xlsx.utils.book_append_sheet(workBook, workSheet, "Statistiques");
      xlsx.writeFile(
        workBook,
        //? Format string to filename
        title.normalize("NFD") + ".xlsx"
      );
    });
  };

  const pieProps = {
    data,
    margin: { top: 80, right: 80, bottom: 80, left: 80 },
    innerRadius: 0.5,
    padAngle: 0.7,
    cornerRadius: 3,
    activeOuterRadiusOffset: 8,
    borderWidth: 1,
    colors: { scheme: colors || "nivo" },
    borderColor: { from: "color", modifiers: [["darker", 0.2]] },
    arcLinkLabelsSkipAngle: 10,
    arcLinkLabelsTextColor: "#333333",
    arcLinkLabelsThickness: 2,
    arcLinkLabelsColor: { from: "color" },
    arcLabelsSkipAngle: 10,
    arcLabelsTextColor: { from: "color", modifiers: [["darker", 2]] },
    arcLabel: (e) => {
      if (displayPercentage) {
        const total = data.reduce((a, b) => a + b.value, 0);
        const percentage = (e.value / total) * 100;
        return `${e.value} (${percentage.toFixed(1)}%)`;
      } else {
        return e.value;
      }
    },
    defs: [
      {
        id: "dots",
        type: "patternDots",
        background: "inherit",
        color: "rgba(255, 255, 255, 0.3)",
        size: 4,
        padding: 1,
        stagger: true,
      },
      {
        id: "lines",
        type: "patternLines",
        background: "inherit",
        color: "rgba(255, 255, 255, 0.3)",
        rotation: -45,
        lineWidth: 6,
        spacing: 10,
      },
    ],
    fill: [
      {
        match: {
          id: "ruby",
        },
        id: "dots",
      },
      {
        match: {
          id: "c",
        },
        id: "dots",
      },
      {
        match: {
          id: "go",
        },
        id: "dots",
      },
      {
        match: {
          id: "python",
        },
        id: "dots",
      },
      {
        match: {
          id: "scala",
        },
        id: "lines",
      },
      {
        match: {
          id: "lisp",
        },
        id: "lines",
      },
      {
        match: {
          id: "elixir",
        },
        id: "lines",
      },
      {
        match: {
          id: "javascript",
        },
        id: "lines",
      },
    ],
    layers: [
      "arcs",
      "arcLabels",
      "arcLinkLabels",
      "legends",
      ({ dataWithArc, centerX, centerY }) => {
        let total = 0;
        dataWithArc.forEach((element) => {
          total += element.value;
        });
        return (
          <text
            x={centerX}
            y={centerY}
            textAnchor="middle"
            dominantBaseline="central"
            style={{
              fontSize: "2.5rem",
              fontWeight: "bold",
              fill: "#333333",
            }}
          >
            {total}
          </text>
        );
      },
      ({ centerX }) => {
        return (
          <text
            x={centerX}
            y={-55}
            textAnchor="middle"
            dominantBaseline="central"
            style={{
              fontSize: "1 rem",
              fontWeight: "bold",
              fill: "#333333",
            }}
          >
            {title}
          </text>
        );
      },
    ],
    legends: displayLegends
      ? [
          {
            anchor: "bottom",
            direction: "row",
            justify: false,
            translateX: 0,
            translateY: 56,
            itemsSpacing: 0,
            itemWidth: legendWidth || 100,
            itemHeight: 18,
            itemTextColor: "#999",
            itemDirection: "left-to-right",
            itemOpacity: 1,
            symbolSize: 18,
            symbolShape: "circle",
            effects: [
              {
                on: "hover",
                style: {
                  itemTextColor: "#000",
                },
              },
            ],
          },
        ]
      : undefined,
  };

  return (
    <>
      {data && Array.isArray(data) && data.length > 0 ? (
        <React.Fragment>
          <div
            style={{
              height: graphHeight,
            }}
          >
            <ResponsivePie {...pieProps} onClick={() => setDialogOpen(true)} />
          </div>
          <div className="align-items-center export-buttons stats_container__component-export">
            <Button
              type="button"
              icon="pi pi-file-excel"
              onClick={exportPieChartExcel}
              className="p-button-success xl"
              data-pr-tooltip="Exporter sous format Excel"
            />
          </div>
        </React.Fragment>
      ) : (
        <Loader />
      )}
      <Modal
        visible={dialogOpen}
        setVisible={setDialogOpen}
        header={title}
        height="80vh"
        blockScroll
      >
        <PieCanvas
          {...pieProps}
          height={graphHeight}
          width={window.innerWidth - 100}
          onClick={() => setDialogOpen(false)}
          theme={{
            background: "#FFFFFF",
          }}
        />
      </Modal>
    </>
  );
};

NivoPie.propTypes = {
  data: PropTypes.any,
  displayPercentage: PropTypes.bool,
  labelName: PropTypes.string,
  legendWidth: PropTypes.number,
  title: PropTypes.string,
  graphHeight: PropTypes.number,
  colors: PropTypes.string,
  displayLegends: PropTypes.bool,
};

NivoPie.defaultProps = {
  displayPercentage: false,
  labelName: "Catégorie",
  legendWidth: 100,
  title: "Titre",
  graphHeight: 600,
  colors: "nivo",
  displayLegends: true,
};

export default NivoPie;
